import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import CatalogCategoryOnboardingBanner from "../../../../Components/Studio/CatalogCategory/CatalogCategoryOnboardingBanner";
import CatalogCategoryWizard from "../../../../Components/Studio/CatalogCategory/CatalogCategoryWizard";
import SmartImportWizard from "../../../../Components/Studio/SmartImportWizard";
import usePlanFeatures from "../../../../hooks/usePlanFeatures";
import { invalidateSessionCapabilities } from "../../../../services/sessionCapabilitiesService";
import { groupCatalogItemsByCategory } from "../../../../helpers/catalogCategoryGrouping";
import { fetchAllPaginated } from "../../../../helpers/fetchAllPaginated";
import { userHasAnyRole } from "../../../../services/authService";
import { ADMIN_ROLES } from "../../../../Routes/roleAccess";
import ServiceCoverageNotice from "../../../../Components/Studio/ServiceCoverageNotice";
import { useCatalogCategoryOnboarding } from "../../../../hooks/useCatalogCategoryOnboarding";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  getHelpArticleUrl,
  HELP_ARTICLE_SERVICES,
  HELP_ARTICLE_SERVICE_BUNDLES,
} from "../../../../helpers/helpCenter";
import { resolveCategoryLabel } from "../../../../helpers/catalogCategoryDisplay";
import {
  datetimeLocalValueToUtcIso,
  utcIsoToDatetimeLocalValue,
} from "../../../../helpers/datetimeUtc";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  countEnabledProfessionals,
  getServiceProfessionals,
  ServiceProfessionalsData,
  syncServiceProfessionals,
} from "../../../../services/professionalServiceEnablementService";
import { getProducts, Product } from "../../../../services/productService";
import { getCatalogCategories } from "../../../../services/catalogCategoryService";
import {
  createService,
  CreateServicePayload,
  deleteService,
  getService,
  getServices,
  Service,
  syncServiceRecipe,
  updateService,
} from "../../../../services/serviceService";
import { getServicePackages } from "../../../../services/servicePackageService";
import { getSettings } from "../../../../services/tenantSettingService";
import {
  ClientRecordTemplate,
  getAll as getHealthTemplates,
} from "../../../../services/clientRecordTemplateService";
import { getUnits, Unit } from "../../../../services/unitService";
import { PaginationMeta } from "../../../../types/api";
import { CatalogCategory } from "../../../../types/catalogCategory";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const CATALOG_PAGE_SIZE = 100;
const DEPOSIT_PERCENT_BPS_OPTIONS = [1000, 2000, 5000, 10000] as const;
const BUFFER_PRESET_MINUTES = [0, 10, 15, 30] as const;

interface ServiceFormValues {
  unit_id: string;
  name: string;
  description: string;
  duration_minutes: number;
  buffer_minutes: number;
  price_display: string;
  price_is_from: boolean;
  promo_enabled: boolean;
  promo_price_display: string;
  promo_starts_at_local: string;
  promo_ends_at_local: string;
  category_id: string;
  client_record_template_id: string;
  require_deposit: boolean;
  deposit_percent_selections: string[];
  return_reminder_enabled: boolean;
  return_interval_days: number;
  loyalty_enabled: boolean;
  loyalty_stamps_required: number;
  loyalty_reward_validity_days: string;
}

interface RecipeRow {
  key: string;
  product_id: string;
  quantity_consumed: number;
}

const emptyFormValues = (): ServiceFormValues => ({
  unit_id: "",
  name: "",
  description: "",
  duration_minutes: 30,
  buffer_minutes: 0,
  price_display: "",
  price_is_from: false,
  promo_enabled: false,
  promo_price_display: "",
  promo_starts_at_local: "",
  promo_ends_at_local: "",
  category_id: "",
  client_record_template_id: "",
  require_deposit: false,
  deposit_percent_selections: [],
  return_reminder_enabled: false,
  return_interval_days: 30,
  loyalty_enabled: false,
  loyalty_stamps_required: 10,
  loyalty_reward_validity_days: "",
});

const mapServiceToFormValues = (service: Service): ServiceFormValues => {
  const hasPromo =
    service.promo_price_cents != null &&
    Boolean(service.promo_starts_at) &&
    Boolean(service.promo_ends_at);

  return {
    unit_id: String(service.unit_id),
    name: service.name,
    description: service.description ?? "",
    duration_minutes: service.duration_minutes,
    buffer_minutes: service.buffer_minutes ?? 0,
    price_display: formatCentsToMoneyInput(service.price),
    price_is_from: Boolean(service.price_is_from),
    promo_enabled: hasPromo,
    promo_price_display: hasPromo
      ? formatCentsToMoneyInput(service.promo_price_cents as number)
      : "",
    promo_starts_at_local:
      hasPromo && service.promo_starts_at
        ? utcIsoToDatetimeLocalValue(service.promo_starts_at)
        : "",
    promo_ends_at_local:
      hasPromo && service.promo_ends_at
        ? utcIsoToDatetimeLocalValue(service.promo_ends_at)
        : "",
    category_id: service.category_id ? String(service.category_id) : "",
    client_record_template_id: service.client_record_template_id
      ? String(service.client_record_template_id)
      : "",
    require_deposit: service.require_deposit,
    deposit_percent_selections: (service.deposit_percent_options ?? []).map(String),
    return_reminder_enabled: Boolean(service.return_reminder_enabled),
    return_interval_days: service.return_interval_days ?? 30,
    loyalty_enabled: Boolean(service.loyalty_enabled),
    loyalty_stamps_required: service.loyalty_stamps_required ?? 10,
    loyalty_reward_validity_days:
      service.loyalty_reward_validity_days != null
        ? String(service.loyalty_reward_validity_days)
        : "",
  };
};

const buildServicePayload = (
  values: ServiceFormValues,
  useCatalogCategories: boolean,
  anamnesisPerService: boolean
): CreateServicePayload => ({
  unit_id: Number(values.unit_id),
  name: values.name.trim(),
  description: values.description.trim() !== "" ? values.description.trim() : null,
  duration_minutes: Number(values.duration_minutes),
  buffer_minutes: Number(values.buffer_minutes) || 0,
  price: parseMoneyInputToCents(values.price_display),
  price_is_from: values.price_is_from,
  promo_price_cents: values.promo_enabled
    ? parseMoneyInputToCents(values.promo_price_display)
    : null,
  promo_starts_at: values.promo_enabled
    ? datetimeLocalValueToUtcIso(values.promo_starts_at_local)
    : null,
  promo_ends_at: values.promo_enabled
    ? datetimeLocalValueToUtcIso(values.promo_ends_at_local)
    : null,
  is_active: true,
  require_deposit: values.require_deposit,
  deposit_percent_options: values.require_deposit
    ? values.deposit_percent_selections.map(Number)
    : null,
  return_reminder_enabled: values.return_reminder_enabled,
  return_interval_days: values.return_reminder_enabled
    ? Number(values.return_interval_days)
    : null,
  loyalty_enabled: values.loyalty_enabled,
  loyalty_stamps_required: values.loyalty_enabled
    ? Number(values.loyalty_stamps_required)
    : null,
  loyalty_reward_validity_days:
    values.loyalty_enabled && values.loyalty_reward_validity_days.trim() !== ""
      ? Number(values.loyalty_reward_validity_days)
      : null,
  ...(useCatalogCategories
    ? { category_id: values.category_id ? Number(values.category_id) : null }
    : {}),
  ...(anamnesisPerService
    ? {
        client_record_template_id: values.client_record_template_id
          ? Number(values.client_record_template_id)
          : null,
      }
    : {}),
});

const newRecipeRow = (): RecipeRow => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  product_id: "",
  quantity_consumed: 1,
});

const mapServiceProductsToRecipeRows = (service: Service): RecipeRow[] =>
  (service.products ?? []).map((product) => ({
    key: `product-${product.id}`,
    product_id: String(product.id),
    quantity_consumed:
      (product as Product & { pivot?: { quantity_consumed?: number } }).pivot
        ?.quantity_consumed ?? 1,
  }));

type ServiceFormTab = "data" | "enablement";

const ServicesList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    maxServices,
    servicesCount,
    isAtServiceLimit,
    refresh: refreshPlanFeatures,
  } = usePlanFeatures();

  document.title = t("studio.services.pageTitle");

  const canConfigureCategories = userHasAnyRole(["owner"]);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const [services, setServices] = useState<Service[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Service | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const [recipeOpen, setRecipeOpen] = useState(false);
  const [recipeService, setRecipeService] = useState<Service | null>(null);
  const [recipeRows, setRecipeRows] = useState<RecipeRow[]>([]);
  const [internalProducts, setInternalProducts] = useState<Product[]>([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [useCatalogCategories, setUseCatalogCategories] = useState(false);
  const [anamnesisPerService, setAnamnesisPerService] = useState(false);
  const [healthTemplates, setHealthTemplates] = useState<ClientRecordTemplate[]>([]);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [restrictProfessionalServices, setRestrictProfessionalServices] = useState(false);
  const [useCommissionTracking, setUseCommissionTracking] = useState(true);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [serviceFormTab, setServiceFormTab] = useState<ServiceFormTab>("data");
  const [enablementLoading, setEnablementLoading] = useState(false);
  const [enablementData, setEnablementData] = useState<ServiceProfessionalsData | null>(null);
  const [enablementSearch, setEnablementSearch] = useState("");
  const [selectedProfessionalIds, setSelectedProfessionalIds] = useState<number[]>([]);
  const [enablementCounts, setEnablementCounts] = useState<Record<number, number>>({});
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    if (searchParams.get("import") !== "1") {
      return;
    }
    setImportModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("import");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const notAvailable = t("studio.services.common.notAvailable");
  const generalCategoryLabel = t("studio.catalog.general");

  const bundlesHelpUrl = useMemo(
    () => getHelpArticleUrl(HELP_ARTICLE_SERVICE_BUNDLES, i18n.language),
    [i18n.language]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.services.validation.unitRequired")),
        name: Yup.string().trim().required(t("studio.services.validation.nameRequired")),
        description: Yup.string()
          .trim()
          .max(5000, t("studio.services.fields.description.max")),
        duration_minutes: Yup.number()
          .typeError(t("studio.services.validation.durationType"))
          .min(1, t("studio.services.validation.durationMin"))
          .required(t("studio.services.validation.durationRequired")),
        buffer_minutes: Yup.number()
          .transform((value, original) =>
            original === "" || original === null ? 0 : value
          )
          .typeError(t("studio.services.validation.bufferType"))
          .integer(t("studio.services.validation.bufferType"))
          .min(0, t("studio.services.validation.bufferMin"))
          .max(120, t("studio.services.validation.bufferMax")),
        price_display: Yup.string()
          .required(t("studio.services.validation.priceRequired"))
          .test("price-valid", t("studio.services.validation.priceInvalid"), (value) => {
            return parseMoneyInputToCents(value || "") >= 0;
          }),
        promo_price_display: Yup.string().when("promo_enabled", {
          is: true,
          then: (schema) =>
            schema
              .required(t("studio.services.validation.promoPriceRequired"))
              .test(
                "promo-price-valid",
                t("studio.services.validation.promoPriceInvalid"),
                (value) => parseMoneyInputToCents(value || "") >= 0
              )
              .test(
                "promo-price-below",
                t("studio.services.validation.promoPriceAboveCatalog"),
                function (value) {
                  const catalog = parseMoneyInputToCents(this.parent.price_display || "");
                  const promo = parseMoneyInputToCents(value || "");
                  return promo <= catalog;
                }
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        promo_starts_at_local: Yup.string().when("promo_enabled", {
          is: true,
          then: (schema) =>
            schema.required(t("studio.services.validation.promoStartsRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        promo_ends_at_local: Yup.string().when("promo_enabled", {
          is: true,
          then: (schema) =>
            schema
              .required(t("studio.services.validation.promoEndsRequired"))
              .test(
                "promo-ends-after",
                t("studio.services.validation.promoEndsAfter"),
                function (value) {
                  const start = this.parent.promo_starts_at_local as string;
                  if (!start || !value) {
                    return true;
                  }
                  return new Date(value).getTime() >= new Date(start).getTime();
                }
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
        ...(useCatalogCategories
          ? {
              category_id: Yup.string().nullable().notRequired(),
            }
          : {}),
        deposit_percent_selections: Yup.array().when("require_deposit", {
          is: true,
          then: (schema) =>
            schema
              .min(1, t("studio.services.validation.depositOptionsRequired"))
              .required(t("studio.services.validation.depositOptionsRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        loyalty_stamps_required: Yup.number().when("loyalty_enabled", {
          is: true,
          then: (schema) =>
            schema
              .typeError(t("studio.services.validation.loyaltyStampsType"))
              .integer(t("studio.services.validation.loyaltyStampsType"))
              .min(2, t("studio.services.validation.loyaltyStampsMin"))
              .max(50, t("studio.services.validation.loyaltyStampsMax"))
              .required(t("studio.services.validation.loyaltyStampsRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
        return_interval_days: Yup.number().when("return_reminder_enabled", {
          is: true,
          then: (schema) =>
            schema
              .typeError(t("studio.services.validation.returnIntervalType"))
              .integer(t("studio.services.validation.returnIntervalType"))
              .min(7, t("studio.services.validation.returnIntervalMin"))
              .max(365, t("studio.services.validation.returnIntervalMax"))
              .required(t("studio.services.validation.returnIntervalRequired")),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [t, useCatalogCategories]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, unitFilter]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnits({ per_page: CATALOG_PAGE_SIZE });
      setUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.loadError"));
    }
  }, [t]);

  const loadCatalogConfig = useCallback(async () => {
    try {
      const settingsResponse = await getSettings();
      const enabled = settingsResponse.data.use_catalog_categories;
      const perService =
        settingsResponse.data.anamnesis_scope === "per_client_service";
      setTenantId(settingsResponse.data.tenant_id);
      setUseCatalogCategories(enabled);
      setAnamnesisPerService(perService);
      setRestrictProfessionalServices(settingsResponse.data.restrict_professional_services);
      setUseCommissionTracking(settingsResponse.data.use_commission_tracking !== false);

      if (perService) {
        try {
          const templatesResponse = await getHealthTemplates({
            page: 1,
            per_page: CATALOG_PAGE_SIZE,
            is_active: true,
          });
          setHealthTemplates(templatesResponse.data);
        } catch {
          setHealthTemplates([]);
        }
      } else {
        setHealthTemplates([]);
      }

      if (!enabled) {
        setCategories([]);
        return;
      }

      const categoriesResponse = await getCatalogCategories({
        per_page: CATALOG_PAGE_SIZE,
        is_active: true,
      });
      setCategories(categoriesResponse.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.loadError"));
    }
  }, [t]);

  const loadEnablementCounts = useCallback(
    async (serviceList: Service[]) => {
      if (!restrictProfessionalServices || serviceList.length === 0) {
        setEnablementCounts({});
        return;
      }

      try {
        const results = await Promise.all(
          serviceList.map(async (service) => {
            const response = await getServiceProfessionals(service.id);
            return [service.id, countEnabledProfessionals(response.data)] as const;
          })
        );
        setEnablementCounts(Object.fromEntries(results));
      } catch {
        setEnablementCounts({});
      }
    },
    [restrictProfessionalServices]
  );

  const resolveCategoryOptions = useCallback(
    (currentCategory?: CatalogCategory | null): CatalogCategory[] => {
      if (!currentCategory) {
        return categories;
      }

      if (categories.some((category) => category.id === currentCategory.id)) {
        return categories;
      }

      return [...categories, currentCategory];
    },
    [categories]
  );

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams = {
        ...(unitFilter ? { unit_id: Number(unitFilter) } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };

      if (useCatalogCategories) {
        const allServices = await fetchAllPaginated((currentPage, perPage) =>
          getServices({
            ...baseParams,
            page: currentPage,
            per_page: perPage,
          })
        );
        setServices(allServices);
        setMeta(null);
        await loadEnablementCounts(allServices);
        return;
      }

      const response = await getServices({
        ...baseParams,
        page,
        per_page: PER_PAGE,
      });
      setServices(response.data);
      setMeta(response.meta);
      await loadEnablementCounts(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    unitFilter,
    t,
    loadEnablementCounts,
    useCatalogCategories,
  ]);

  useEffect(() => {
    loadUnits();
    loadCatalogConfig();
  }, [loadUnits, loadCatalogConfig]);

  const handleCategoryWizardComplete = useCallback(async () => {
    await loadCatalogConfig();
    await fetchServices();
  }, [fetchServices, loadCatalogConfig]);

  const {
    showBanner: showCategoryBanner,
    categoryWizardOpen,
    openWizard: openCategoryWizard,
    closeWizard: closeCategoryWizard,
    dismissBanner: dismissCategoryBanner,
    completeWizard: completeCategoryWizard,
  } = useCatalogCategoryOnboarding({
    tenantId,
    useCatalogCategories,
    canConfigure: canConfigureCategories,
    onWizardComplete: handleCategoryWizardComplete,
  });

  useEffect(() => {
    if (services.length > 0) {
      loadEnablementCounts(services);
    }
  }, [restrictProfessionalServices, services, loadEnablementCounts]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const formInitialValues = useMemo(
    () =>
      editingService
        ? mapServiceToFormValues(editingService)
        : { ...emptyFormValues(), unit_id: unitFilter || "" },
    [editingService, unitFilter]
  );

  const formik = useFormik<ServiceFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const payload = buildServicePayload(values, useCatalogCategories, anamnesisPerService);

      try {
        if (editingService) {
          const response = await updateService(editingService.id, payload);
          toast.success(response.message);
          closeForm();
          await fetchServices();
        } else {
          const response = await createService(payload);
          toast.success(response.message);
          invalidateSessionCapabilities();
          void refreshPlanFeatures();
          closeForm();
          if (page !== 1) {
            setPage(1);
          } else {
            await fetchServices();
          }

          let shouldPromptPackage = false;
          try {
            const packagesResponse = await getServicePackages({ per_page: 1 });
            shouldPromptPackage =
              (packagesResponse.meta?.total ?? packagesResponse.data.length) === 0;
          } catch {
            shouldPromptPackage = false;
          }

          if (shouldPromptPackage && response.data?.id) {
            const packagePrompt = await Swal.fire({
              title: t("studio.services.afterCreate.packageTitle"),
              text: t("studio.services.afterCreate.packageText", {
                name: response.data.name,
              }),
              icon: "question",
              showCancelButton: true,
              confirmButtonText: t("studio.services.afterCreate.packageConfirm"),
              cancelButtonText: t("studio.services.afterCreate.packageSkip"),
              confirmButtonColor: "#405189",
              cancelButtonColor: "#878a99",
            });

            if (packagePrompt.isConfirmed) {
              const params = new URLSearchParams({
                action: "create",
                unit_id: String(payload.unit_id),
                service_id: String(response.data.id),
              });
              navigate(`/service-packages?${params.toString()}`);
              return;
            }
          }

          if (useCommissionTracking && response.data?.id) {
            const commissionPrompt = await Swal.fire({
              title: t("studio.services.afterCreate.commissionTitle"),
              text: t("studio.services.afterCreate.commissionText", {
                name: response.data.name,
              }),
              icon: "question",
              showCancelButton: true,
              confirmButtonText: t("studio.services.afterCreate.commissionConfirm"),
              cancelButtonText: t("studio.services.afterCreate.commissionSkip"),
              confirmButtonColor: "#405189",
              cancelButtonColor: "#878a99",
            });

            if (commissionPrompt.isConfirmed) {
              const params = new URLSearchParams({
                service_commission: String(response.data.id),
                service_commission_name: response.data.name,
              });
              navigate(`/professionals?${params.toString()}`);
            }
          }
        }
      } catch (error: any) {
        if (error?.code === "FEATURE_LOCKED_PLAN") {
          // Global axios interceptor already showed a friendly toast.
          return;
        }

        toast.error(error?.data || t("studio.services.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeForm = () => {
    setFormOpen(false);
    setEditingService(null);
    setFormLoading(false);
    setServiceFormTab("data");
    setEnablementData(null);
    setEnablementSearch("");
    setSelectedProfessionalIds([]);
  };

  const loadServiceEnablement = useCallback(async (serviceId: number) => {
    setEnablementLoading(true);

    try {
      const response = await getServiceProfessionals(serviceId);
      setEnablementData(response.data);
      setSelectedProfessionalIds(
        response.data.professionals
          .filter((professional) => professional.is_enabled)
          .map((professional) => professional.id)
      );
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.enablement.toast.loadError"));
    } finally {
      setEnablementLoading(false);
    }
  }, [t]);

  const saveServiceEnablement = async () => {
    if (!editingService) {
      return;
    }

    setSubmitting(true);

    try {
      const response = await syncServiceProfessionals(
        editingService.id,
        selectedProfessionalIds
      );
      toast.success(response.message || t("studio.services.enablement.toast.saveSuccess"));
      setEnablementData(response.data);
      if (!response.data.no_op) {
        setEnablementCounts((current) => ({
          ...current,
          [editingService.id]: countEnabledProfessionals(response.data),
        }));
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.enablement.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnablementProfessional = (professionalId: number, checked: boolean) => {
    setSelectedProfessionalIds((current) => {
      if (checked) {
        return current.includes(professionalId)
          ? current
          : [...current, professionalId];
      }

      return current.filter((id) => id !== professionalId);
    });
  };

  const openCreate = () => {
    if (isAtServiceLimit) {
      toast.info(
        t("plan.services.limitReachedToast", {
          max: maxServices ?? servicesCount,
        })
      );
      return;
    }

    setEditingService(null);
    setFormLoading(false);
    setServiceFormTab("data");
    setFormOpen(true);
  };

  const openEdit = async (
    service: Service,
    options?: { focusHealthTemplate?: boolean }
  ) => {
    setEditingService(null);
    setFormLoading(true);
    setServiceFormTab("data");
    setEnablementData(null);
    setEnablementSearch("");
    setSelectedProfessionalIds([]);
    setFormOpen(true);

    try {
      const response = await getService(service.id);
      setEditingService(response.data);

      if (options?.focusHealthTemplate && anamnesisPerService) {
        window.setTimeout(() => {
          const field = document.getElementById("service-health-template");
          if (field instanceof HTMLElement) {
            field.scrollIntoView({ behavior: "smooth", block: "center" });
            field.focus();
            field.classList.add("border-warning");
            window.setTimeout(() => {
              field.classList.remove("border-warning");
            }, 2500);
          }
        }, 250);
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.loadError"));
      closeForm();
    } finally {
      setFormLoading(false);
    }
  };

  const handleServiceFormTabChange = (tab: ServiceFormTab) => {
    setServiceFormTab(tab);
  };

  useEffect(() => {
    if (
      serviceFormTab !== "enablement" ||
      !restrictProfessionalServices ||
      !editingService ||
      enablementData?.service_id === editingService.id
    ) {
      return;
    }

    void loadServiceEnablement(editingService.id);
  }, [serviceFormTab, restrictProfessionalServices, editingService, enablementData, loadServiceEnablement]);

  const toggleDepositPercent = (bps: number, checked: boolean) => {
    const value = String(bps);
    const current = formik.values.deposit_percent_selections;
    const next = checked
      ? [...current, value]
      : current.filter((item) => item !== value);

    formik.setFieldValue("deposit_percent_selections", next);
    formik.setFieldTouched("deposit_percent_selections", true, false);
  };

  const openRecipe = async (service: Service) => {
    setRecipeOpen(true);
    setRecipeLoading(true);
    setRecipeService(service);

    try {
      const [serviceResponse, productsResponse] = await Promise.all([
        getService(service.id),
        getProducts({
          unit_id: service.unit_id,
          product_type: "internal",
          per_page: CATALOG_PAGE_SIZE,
        }),
      ]);

      setRecipeService(serviceResponse.data);
      setInternalProducts(productsResponse.data);

      const rows = mapServiceProductsToRecipeRows(serviceResponse.data);
      setRecipeRows(rows.length > 0 ? rows : [newRecipeRow()]);
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.recipeLoadError"));
      setRecipeOpen(false);
    } finally {
      setRecipeLoading(false);
    }
  };

  const closeRecipe = () => {
    setRecipeOpen(false);
    setRecipeService(null);
    setRecipeRows([]);
    setInternalProducts([]);
  };

  const saveRecipe = async () => {
    if (!recipeService) {
      return;
    }

    const items = recipeRows
      .filter((row) => row.product_id !== "" && row.quantity_consumed >= 1)
      .map((row) => ({
        product_id: Number(row.product_id),
        quantity_consumed: Number(row.quantity_consumed),
      }));

    const productIds = items.map((item) => item.product_id);
    if (new Set(productIds).size !== productIds.length) {
      toast.error(t("studio.services.toast.recipeDuplicateProduct"));
      return;
    }

    setSubmitting(true);
    try {
      const response = await syncServiceRecipe(recipeService.id, items);
      toast.success(response.message);
      closeRecipe();
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.recipeSaveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await deleteService(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      const isLastOnPage = services.length === 1 && page > 1;
      if (isLastOnPage) {
        setPage(page - 1);
      } else {
        await fetchServices();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.services.toast.deleteError"));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveUnitName = (unitId: number): string =>
    units.find((unit) => unit.id === unitId)?.name ?? notAvailable;

  const resolveServiceCategoryLabel = (service: Service): string =>
    resolveCategoryLabel(service.category, generalCategoryLabel);

  const categoryOptions = resolveCategoryOptions(editingService?.category ?? null);

  const servicesMissingHealthTemplate = useMemo(() => {
    if (!anamnesisPerService) {
      return [];
    }

    return services.filter((service) => !service.client_record_template_id);
  }, [anamnesisPerService, services]);

  const groupedServices = useMemo(() => {
    if (!useCatalogCategories) {
      return null;
    }

    return groupCatalogItemsByCategory(services, categories, generalCategoryLabel);
  }, [categories, generalCategoryLabel, services, useCatalogCategories]);

  const tableColumnCount =
    6 +
    (useCatalogCategories && !groupedServices ? 1 : 0) +
    (restrictProfessionalServices ? 1 : 0);

  const filteredEnablementProfessionals = useMemo(() => {
    if (!enablementData) {
      return [];
    }

    const term = enablementSearch.trim().toLowerCase();

    return enablementData.professionals.filter((professional) =>
      term === "" ? true : professional.name.toLowerCase().includes(term)
    );
  }, [enablementData, enablementSearch]);

  const updateRecipeRow = (key: string, patch: Partial<RecipeRow>) => {
    setRecipeRows((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  };

  const removeRecipeRow = (key: string) => {
    setRecipeRows((rows) => rows.filter((row) => row.key !== key));
  };

  const renderServiceRow = (service: Service) => (
    <tr key={service.id}>
      <td>
        <div className="d-flex flex-wrap align-items-center gap-1">
          <span>{service.name}</span>
          {service.price_is_from ? (
            <Badge color="soft-warning" className="fw-normal">
              {t("studio.services.table.priceFromBadge")}
            </Badge>
          ) : null}
          {service.promo_active ? (
            <Badge color="soft-danger" className="fw-normal">
              {t("studio.services.table.promoBadge")}
            </Badge>
          ) : null}
          {service.loyalty_enabled ? (
            <Badge color="soft-success" className="fw-normal">
              {t("studio.services.table.loyaltyBadge", {
                count: service.loyalty_stamps_required ?? 0,
              })}
            </Badge>
          ) : null}
          {service.loyalty_card_id ? (
            <Badge color="soft-primary" className="fw-normal" title={service.loyalty_card_name ?? undefined}>
              {t("studio.services.table.loyaltyMultiBadge", {
                name: service.loyalty_card_name ?? "",
              })}
            </Badge>
          ) : null}
          {service.return_reminder_enabled ? (
            <Badge color="soft-info" className="fw-normal">
              {t("studio.services.table.returnBadge", {
                days: service.return_interval_days ?? 0,
              })}
            </Badge>
          ) : null}
        </div>
      </td>
      <td>{resolveUnitName(service.unit_id)}</td>
      {useCatalogCategories && !groupedServices ? (
        <td>{resolveServiceCategoryLabel(service)}</td>
      ) : null}
      <td>
        {t("studio.catalog.services.durationValue", {
          minutes: service.duration_minutes,
          abbr: t("studio.catalog.services.minutes_abbr"),
        })}
        {service.buffer_minutes > 0 ? (
          <Badge
            color="light"
            className="ms-2 text-muted fw-normal"
            title={t("studio.services.table.bufferBadgeTooltip")}
          >
            <i className="ri-brush-line align-bottom me-1" />
            {t("studio.services.table.bufferBadge", {
              minutes: service.buffer_minutes,
              abbr: t("studio.catalog.services.minutes_abbr"),
            })}
          </Badge>
        ) : null}
      </td>
      <td>
        {service.require_deposit ? (
          <Badge color="warning" className="text-uppercase">
            {t("studio.services.deposit.badge")}
          </Badge>
        ) : (
          <span className="text-muted">{notAvailable}</span>
        )}
      </td>
      {restrictProfessionalServices ? (
        <td>
          <Badge color="info" className="badge-soft-info">
            {t("studio.services.table.professionalsBadge", {
              count: enablementCounts[service.id] ?? 0,
            })}
          </Badge>
        </td>
      ) : null}
      <td>
        <Badge color={service.is_active ? "success" : "secondary"}>
          {service.is_active
            ? t("studio.services.status.active")
            : t("studio.services.status.inactive")}
        </Badge>
      </td>
      <td>
        {service.promo_active
          ? service.price_is_from
            ? t("studio.services.price.fromPromo", {
                price: formatMoney(service.effective_price ?? service.price),
                list: formatMoney(service.price),
              })
            : t("studio.services.price.promo", {
                price: formatMoney(service.effective_price ?? service.price),
                list: formatMoney(service.price),
              })
          : service.price_is_from
            ? t("studio.services.price.from", { price: formatMoney(service.price) })
            : formatMoney(service.price)}
      </td>
      <td className="text-end">
        <Button
          color="soft-info"
          size="sm"
          className="me-1"
          title={t("studio.services.actions.recipe")}
          onClick={() => openRecipe(service)}
        >
          <i className="ri-flask-line" />
        </Button>
        <Button
          color="soft-primary"
          size="sm"
          className="me-1"
          title={t("studio.services.actions.edit")}
          onClick={() => openEdit(service)}
        >
          <i className="ri-pencil-line" />
        </Button>
        <Button
          color="soft-danger"
          size="sm"
          title={t("studio.services.actions.delete")}
          onClick={() => {
            setDeleteTarget(service);
            setDeleteModal(true);
          }}
        >
          <i className="ri-delete-bin-line" />
        </Button>
      </td>
    </tr>
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.services.breadcrumb.title")}
          pageTitle={t("studio.services.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title mb-0 flex-grow-1">{t("studio.services.card.title")}</h4>
                <HelpArticleLink articlePath={HELP_ARTICLE_SERVICES} className="align-self-center" />
                <div className="d-flex flex-wrap gap-2">
                  <Button color="soft-primary" onClick={() => setImportModalOpen(true)}>
                    <i className="ri-upload-2-line align-bottom me-1" />
                    {t("imports.wizard.button.services")}
                  </Button>
                  <Button
                    color="primary"
                    onClick={openCreate}
                    disabled={isAtServiceLimit}
                    title={
                      isAtServiceLimit
                        ? t("plan.services.limitReachedToast", {
                            max: maxServices ?? servicesCount,
                          })
                        : undefined
                    }
                  >
                    <i className="ri-add-line align-bottom me-1" />
                    {t("studio.services.actions.new")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {isAtServiceLimit ? (
                  <Alert color="warning" className="mb-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <span>
                        {t("plan.services.limitReached", {
                          current: servicesCount,
                          max: maxServices ?? servicesCount,
                        })}
                      </span>
                      <Link
                        to="/settings/subscription"
                        className="btn btn-sm btn-warning"
                      >
                        {t("plan.upgrade.cta")}
                      </Link>
                    </div>
                  </Alert>
                ) : maxServices !== null ? (
                  <Alert color="info" className="mb-3 py-2">
                    {t("plan.services.usageHint", {
                      current: servicesCount,
                      max: maxServices,
                    })}
                  </Alert>
                ) : null}
                {servicesMissingHealthTemplate.length > 0 ? (
                  <Alert color="warning" className="mb-3">
                    <p className="mb-2">
                      {t("studio.services.anamnesis.missingTemplateBanner", {
                        count: servicesMissingHealthTemplate.length,
                      })}
                    </p>
                    <p className="text-muted small mb-2">
                      {t("studio.services.anamnesis.missingTemplateHint")}
                    </p>
                    <ul className="mb-0 small ps-3">
                      {servicesMissingHealthTemplate.slice(0, 8).map((service) => (
                        <li key={service.id}>
                          <Button
                            color="link"
                            className="p-0 align-baseline"
                            type="button"
                            onClick={() =>
                              openEdit(service, { focusHealthTemplate: true })
                            }
                          >
                            {service.name}
                          </Button>
                        </li>
                      ))}
                      {servicesMissingHealthTemplate.length > 8 ? (
                        <li className="text-muted">
                          {t("studio.services.anamnesis.missingTemplateMore", {
                            count: servicesMissingHealthTemplate.length - 8,
                          })}
                        </li>
                      ) : null}
                    </ul>
                  </Alert>
                ) : null}
                <Alert color="info" className="mb-3 py-2">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div className="d-flex flex-column gap-1">
                      <span>{t("studio.services.discovery.bundlesBanner")}</span>
                      <a
                        href={bundlesHelpUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="small fw-medium"
                      >
                        <i className="ri-question-line align-bottom me-1" />
                        {t("studio.services.discovery.bundlesHelpLink")}
                      </a>
                    </div>
                    <Button
                      color="info"
                      size="sm"
                      outline
                      tag={Link}
                      to="/service-bundles"
                    >
                      {t("studio.services.discovery.bundlesAction")}
                    </Button>
                  </div>
                </Alert>

                {!useCatalogCategories && showCategoryBanner ? (
                  <CatalogCategoryOnboardingBanner
                    canConfigure={canConfigureCategories}
                    onStartWizard={openCategoryWizard}
                    onDismiss={dismissCategoryBanner}
                  />
                ) : null}

                <Row className="g-2 mb-3">
                  <Col md={4}>
                    <Input
                      type="select"
                      value={unitFilter}
                      onChange={(event) => setUnitFilter(event.target.value)}
                      aria-label={t("studio.services.filter.unit")}
                    >
                      <option value="">{t("studio.services.filter.allUnits")}</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={8}>
                    <Input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={t("studio.services.search.placeholder")}
                    />
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary">{t("studio.services.loading")}</Spinner>
                  </div>
                ) : services.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {debouncedSearch
                        ? t("studio.services.empty.noResults", { term: debouncedSearch })
                        : t("studio.services.empty.noData")}
                    </p>
                    <Button
                      color="primary"
                      onClick={openCreate}
                      disabled={isAtServiceLimit}
                    >
                      {t("studio.services.actions.createFirst")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.services.table.name")}</th>
                            <th>{t("studio.services.table.unit")}</th>
                            {useCatalogCategories && !groupedServices ? (
                              <th>{t("studio.catalog.category")}</th>
                            ) : null}
                            <th>{t("studio.catalog.services.fields.duration")}</th>
                            <th>{t("studio.services.table.deposit")}</th>
                            {restrictProfessionalServices ? (
                              <th>{t("studio.services.table.professionals")}</th>
                            ) : null}
                            <th>{t("studio.services.table.status")}</th>
                            <th>{t("studio.services.table.price")}</th>
                            <th className="text-end">{t("studio.services.table.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedServices
                            ? groupedServices.flatMap((group) => [
                                <tr key={`group-${group.groupKey}`} className="table-light">
                                  <td colSpan={tableColumnCount}>
                                    <span className="fw-semibold">{group.label}</span>
                                    <span className="text-muted ms-2 small">
                                      {t("studio.catalog.onboarding.groupCount", {
                                        count: group.services.length,
                                      })}
                                    </span>
                                  </td>
                                </tr>,
                                ...group.services.map((service) => renderServiceRow(service)),
                              ])
                            : services.map((service) => renderServiceRow(service))}
                        </tbody>
                      </Table>
                    </div>
                    {meta && meta.last_page > 1 && !useCatalogCategories ? (
                      <Row className="align-items-center mt-3">
                        <Col sm={6}>
                          <div className="text-muted">
                            {t("studio.services.pagination.showing", {
                              from: meta.from ?? 0,
                              to: meta.to ?? 0,
                              total: meta.total,
                            })}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Pagination className="pagination-separated justify-content-sm-end mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page > 1) {
                                    setPage(page - 1);
                                  }
                                }}
                              >
                                {t("studio.services.pagination.previous")}
                              </PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={page >= meta.last_page}>
                              <PaginationLink
                                next
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page < meta.last_page) {
                                    setPage(page + 1);
                                  }
                                }}
                              >
                                {t("studio.services.pagination.next")}
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas isOpen={formOpen} toggle={closeForm} direction="end">
        <OffcanvasHeader toggle={closeForm}>
          {editingService
            ? t("studio.services.form.editTitle")
            : t("studio.services.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
            <>
              {editingService ? (
                <Nav pills className="nav-pills-custom nav-primary gap-2 mb-4">
                  <NavItem>
                    <NavLink
                      href="#"
                      className={serviceFormTab === "data" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleServiceFormTabChange("data");
                      }}
                    >
                      {t("studio.services.form.tabs.data")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      href="#"
                      className={serviceFormTab === "enablement" ? "active" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        handleServiceFormTabChange("enablement");
                      }}
                    >
                      {t("studio.services.form.tabs.enablement")}
                    </NavLink>
                  </NavItem>
                </Nav>
              ) : null}

              {serviceFormTab === "data" || !editingService ? (
          <Form key={entityFormKey(editingService?.id)} onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <Label htmlFor="service-unit">{t("studio.services.fields.unit.label")}</Label>
              <Input
                id="service-unit"
                name="unit_id"
                type="select"
                value={formik.values.unit_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.unit_id && formik.errors.unit_id)}
              >
                <option value="">{t("studio.services.fields.unit.placeholder")}</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Input>
              <FormFeedback>{formik.errors.unit_id}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="service-name">{t("studio.services.fields.name.label")}</Label>
              <Input
                id="service-name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.services.fields.name.placeholder")}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="service-description">
                {t("studio.services.fields.description.label")}
              </Label>
              <Input
                id="service-description"
                name="description"
                type="textarea"
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.services.fields.description.placeholder")}
                invalid={!!(formik.touched.description && formik.errors.description)}
              />
              <FormFeedback>{formik.errors.description}</FormFeedback>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.description.hint")}
              </small>
            </div>
            <div className="mb-3">
              <Label htmlFor="service-duration">
                {t("studio.services.fields.duration.label")}
              </Label>
              <Input
                id="service-duration"
                name="duration_minutes"
                type="number"
                min={1}
                value={formik.values.duration_minutes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(formik.touched.duration_minutes && formik.errors.duration_minutes)
                }
              />
              <FormFeedback>{formik.errors.duration_minutes}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="service-buffer">
                {t("studio.services.fields.bufferMinutes.label")}
              </Label>
              <Input
                id="service-buffer"
                name="buffer_minutes"
                type="number"
                min={0}
                max={120}
                step={5}
                value={formik.values.buffer_minutes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.services.fields.bufferMinutes.placeholder")}
                invalid={
                  !!(formik.touched.buffer_minutes && formik.errors.buffer_minutes)
                }
              />
              <FormFeedback>{formik.errors.buffer_minutes}</FormFeedback>
              <div
                className="d-flex flex-wrap gap-1 mt-2"
                role="group"
                aria-label={t("studio.services.fields.bufferMinutes.presetsLabel")}
              >
                {BUFFER_PRESET_MINUTES.map((preset) => (
                  <Button
                    key={preset}
                    type="button"
                    size="sm"
                    color={Number(formik.values.buffer_minutes) === preset ? "primary" : "light"}
                    onClick={() => {
                      formik.setFieldValue("buffer_minutes", preset);
                      formik.setFieldTouched("buffer_minutes", true, false);
                    }}
                  >
                    {preset === 0
                      ? t("studio.services.fields.bufferMinutes.presetNone")
                      : t("studio.services.fields.bufferMinutes.presetValue", {
                          minutes: preset,
                          abbr: t("studio.catalog.services.minutes_abbr"),
                        })}
                  </Button>
                ))}
              </div>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.bufferMinutes.hint")}
              </small>
            </div>
            <div className="mb-3">
              <Label htmlFor="service-price">{t("studio.services.fields.price.label")}</Label>
              <Input
                id="service-price"
                name="price_display"
                value={formik.values.price_display}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.services.fields.price.placeholder")}
                invalid={!!(formik.touched.price_display && formik.errors.price_display)}
              />
              <FormFeedback>{formik.errors.price_display}</FormFeedback>
            </div>
            <div className="mb-3">
              <div className="form-check form-switch">
                <Input
                  id="service-price-is-from"
                  name="price_is_from"
                  type="switch"
                  role="switch"
                  checked={formik.values.price_is_from}
                  onChange={(event) => {
                    formik.setFieldValue("price_is_from", event.target.checked);
                  }}
                />
                <Label htmlFor="service-price-is-from" className="form-check-label">
                  {t("studio.services.fields.priceIsFrom.label")}
                </Label>
              </div>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.priceIsFrom.hint")}
              </small>
            </div>
            <div className="mb-3">
              <div className="form-check form-switch">
                <Input
                  id="service-promo-enabled"
                  name="promo_enabled"
                  type="switch"
                  role="switch"
                  checked={formik.values.promo_enabled}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    formik.setFieldValue("promo_enabled", enabled);
                    if (!enabled) {
                      formik.setFieldValue("promo_price_display", "");
                      formik.setFieldValue("promo_starts_at_local", "");
                      formik.setFieldValue("promo_ends_at_local", "");
                    }
                  }}
                />
                <Label htmlFor="service-promo-enabled" className="form-check-label">
                  {t("studio.services.fields.promo.enabledLabel")}
                </Label>
              </div>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.promo.enabledHint")}
              </small>
            </div>
            {formik.values.promo_enabled ? (
              <>
                <div className="mb-3">
                  <Label htmlFor="service-promo-price">
                    {t("studio.services.fields.promo.priceLabel")}
                  </Label>
                  <Input
                    id="service-promo-price"
                    name="promo_price_display"
                    value={formik.values.promo_price_display}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder={t("studio.services.fields.promo.pricePlaceholder")}
                    invalid={
                      !!(
                        formik.touched.promo_price_display &&
                        formik.errors.promo_price_display
                      )
                    }
                  />
                  <FormFeedback>{formik.errors.promo_price_display}</FormFeedback>
                </div>
                <div className="mb-3">
                  <Label htmlFor="service-promo-starts">
                    {t("studio.services.fields.promo.startsLabel")}
                  </Label>
                  <Input
                    id="service-promo-starts"
                    name="promo_starts_at_local"
                    type="datetime-local"
                    value={formik.values.promo_starts_at_local}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(
                        formik.touched.promo_starts_at_local &&
                        formik.errors.promo_starts_at_local
                      )
                    }
                  />
                  <FormFeedback>{formik.errors.promo_starts_at_local}</FormFeedback>
                </div>
                <div className="mb-3">
                  <Label htmlFor="service-promo-ends">
                    {t("studio.services.fields.promo.endsLabel")}
                  </Label>
                  <Input
                    id="service-promo-ends"
                    name="promo_ends_at_local"
                    type="datetime-local"
                    value={formik.values.promo_ends_at_local}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={
                      !!(formik.touched.promo_ends_at_local && formik.errors.promo_ends_at_local)
                    }
                  />
                  <FormFeedback>{formik.errors.promo_ends_at_local}</FormFeedback>
                  <small className="text-muted d-block mt-1">
                    {t("studio.services.fields.promo.rangeHint")}
                  </small>
                </div>
              </>
            ) : null}
            <div className="mb-3">
              <div className="form-check form-switch">
                <Input
                  id="service-require-deposit"
                  name="require_deposit"
                  type="switch"
                  role="switch"
                  checked={formik.values.require_deposit}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    formik.setFieldValue("require_deposit", enabled);
                    if (!enabled) {
                      formik.setFieldValue("deposit_percent_selections", []);
                    }
                  }}
                />
                <Label htmlFor="service-require-deposit" className="form-check-label">
                  {t("studio.services.fields.requireDeposit.label")}
                </Label>
              </div>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.requireDeposit.hint")}
              </small>
            </div>
            {formik.values.require_deposit ? (
              <div className="mb-3">
                <Label>{t("studio.services.fields.depositPercentOptions.label")}</Label>
                <div className="d-flex flex-wrap gap-3">
                  {DEPOSIT_PERCENT_BPS_OPTIONS.map((bps) => (
                    <div className="form-check" key={bps}>
                      <Input
                        id={`service-deposit-${bps}`}
                        type="checkbox"
                        checked={formik.values.deposit_percent_selections.includes(String(bps))}
                        onChange={(event) => toggleDepositPercent(bps, event.target.checked)}
                      />
                      <Label htmlFor={`service-deposit-${bps}`} className="form-check-label">
                        {t("studio.services.fields.depositPercentOptions.option", {
                          percent: bps / 100,
                        })}
                      </Label>
                    </div>
                  ))}
                </div>
                {formik.touched.deposit_percent_selections &&
                formik.errors.deposit_percent_selections ? (
                  <div className="invalid-feedback d-block">
                    {formik.errors.deposit_percent_selections as string}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="mb-3">
              <div className="form-check form-switch">
                <Input
                  id="service-return-reminder-enabled"
                  name="return_reminder_enabled"
                  type="switch"
                  role="switch"
                  checked={formik.values.return_reminder_enabled}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    formik.setFieldValue("return_reminder_enabled", enabled);
                    if (enabled && !formik.values.return_interval_days) {
                      formik.setFieldValue("return_interval_days", 30);
                    }
                  }}
                />
                <Label
                  htmlFor="service-return-reminder-enabled"
                  className="form-check-label"
                >
                  {t("studio.services.fields.returnReminderEnabled.label")}
                </Label>
              </div>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.returnReminderEnabled.hint")}{" "}
                <Link to="/marketing/contact-ruler">
                  {t("studio.services.fields.returnReminderEnabled.rulerLink")}
                </Link>
              </small>
            </div>
            {formik.values.return_reminder_enabled ? (
              <div className="mb-3">
                <Label htmlFor="service-return-interval">
                  {t("studio.services.fields.returnIntervalDays.label")}
                </Label>
                <Input
                  id="service-return-interval"
                  name="return_interval_days"
                  type="number"
                  min={7}
                  max={365}
                  value={formik.values.return_interval_days}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(
                      formik.touched.return_interval_days &&
                      formik.errors.return_interval_days
                    )
                  }
                />
                <FormFeedback>{formik.errors.return_interval_days}</FormFeedback>
                <small className="text-muted d-block mt-1">
                  {t("studio.services.fields.returnIntervalDays.hint")}
                </small>
                <div
                  className="d-flex flex-wrap gap-1 mt-2"
                  role="group"
                  aria-label={t("studio.services.fields.returnIntervalDays.presetsLabel")}
                >
                  {[30, 90, 180].map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      size="sm"
                      color={
                        Number(formik.values.return_interval_days) === preset
                          ? "primary"
                          : "light"
                      }
                      onClick={() => {
                        formik.setFieldValue("return_interval_days", preset);
                        formik.setFieldTouched("return_interval_days", true, false);
                      }}
                    >
                      {t("studio.services.fields.returnIntervalDays.presetValue", {
                        days: preset,
                      })}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="mb-3">
              <div className="form-check form-switch">
                <Input
                  id="service-loyalty-enabled"
                  name="loyalty_enabled"
                  type="switch"
                  role="switch"
                  checked={formik.values.loyalty_enabled}
                  onChange={(event) => {
                    const enabled = event.target.checked;
                    formik.setFieldValue("loyalty_enabled", enabled);
                    if (enabled && !formik.values.loyalty_stamps_required) {
                      formik.setFieldValue("loyalty_stamps_required", 10);
                    }
                  }}
                />
                <Label htmlFor="service-loyalty-enabled" className="form-check-label">
                  {t("studio.services.fields.loyaltyEnabled.label")}
                </Label>
              </div>
              <small className="text-muted d-block mt-1">
                {t("studio.services.fields.loyaltyEnabled.hint")}
              </small>
              {!formik.values.loyalty_enabled ? (
                <p className="text-muted small mb-0 mt-2">
                  {t("studio.services.fields.loyaltyEnabled.empty")}
                </p>
              ) : null}
            </div>
            {formik.values.loyalty_enabled ? (
              <div className="mb-3">
                <Label htmlFor="service-loyalty-stamps">
                  {t("studio.services.fields.loyaltyStampsRequired.label")}
                </Label>
                <Input
                  id="service-loyalty-stamps"
                  name="loyalty_stamps_required"
                  type="number"
                  min={2}
                  max={50}
                  value={formik.values.loyalty_stamps_required}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(
                      formik.touched.loyalty_stamps_required &&
                      formik.errors.loyalty_stamps_required
                    )
                  }
                />
                <FormFeedback>{formik.errors.loyalty_stamps_required}</FormFeedback>
                <small className="text-muted d-block mt-1">
                  {t("studio.services.fields.loyaltyStampsRequired.hint")}
                </small>
                <Label htmlFor="service-loyalty-validity" className="mt-3">
                  {t("studio.services.fields.loyaltyRewardValidity.label")}
                </Label>
                <Input
                  id="service-loyalty-validity"
                  name="loyalty_reward_validity_days"
                  type="number"
                  min={1}
                  max={730}
                  placeholder={t(
                    "studio.services.fields.loyaltyRewardValidity.placeholder"
                  )}
                  value={formik.values.loyalty_reward_validity_days}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(
                      formik.touched.loyalty_reward_validity_days &&
                      formik.errors.loyalty_reward_validity_days
                    )
                  }
                />
                <FormFeedback>
                  {formik.errors.loyalty_reward_validity_days}
                </FormFeedback>
                <small className="text-muted d-block mt-1">
                  {t("studio.services.fields.loyaltyRewardValidity.hint")}
                </small>
              </div>
            ) : null}
            {useCatalogCategories ? (
              <div className="mb-3">
                <Label htmlFor="service-category">{t("studio.catalog.category")}</Label>
                <Input
                  id="service-category"
                  name="category_id"
                  type="select"
                  value={formik.values.category_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">{t("studio.catalog.general")}</option>
                  {categoryOptions.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Input>
              </div>
            ) : null}
            {anamnesisPerService ? (
              <div className="mb-3">
                <Label htmlFor="service-health-template">
                  {t("studio.services.fields.healthTemplate.label")}
                </Label>
                <Input
                  id="service-health-template"
                  name="client_record_template_id"
                  type="select"
                  value={formik.values.client_record_template_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">
                    {t("studio.services.fields.healthTemplate.placeholder")}
                  </option>
                  {healthTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title}
                    </option>
                  ))}
                </Input>
                <p className="text-muted small mb-0 mt-1">
                  {t("studio.services.fields.healthTemplate.help")}
                </p>
              </div>
            ) : null}
            <div className="d-flex gap-2 justify-content-end">
              <Button color="light" onClick={closeForm}>
                {t("studio.services.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? t("studio.services.loading") : t("studio.services.actions.save")}
              </Button>
            </div>
          </Form>
              ) : (
                <div>
                  {!restrictProfessionalServices ? (
                    <ServiceCoverageNotice
                      message={t("studio.services.specialty.allProfessionals")}
                      showSettingsLink={userHasAnyRole(ADMIN_ROLES)}
                    />
                  ) : enablementLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary">{t("studio.services.loading")}</Spinner>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted small mb-3">
                        {t("studio.services.enablement.description")}
                      </p>
                      <Input
                        type="search"
                        className="mb-3"
                        placeholder={t("studio.services.enablement.searchPlaceholder")}
                        value={enablementSearch}
                        onChange={(event) => setEnablementSearch(event.target.value)}
                      />
                      {filteredEnablementProfessionals.length === 0 ? (
                        <p className="text-muted">{t("studio.services.enablement.empty")}</p>
                      ) : (
                        <div className="d-flex flex-column gap-2 mb-3">
                          {filteredEnablementProfessionals.map((professional) => (
                            <div className="form-check" key={professional.id}>
                              <Input
                                id={`enablement-professional-${professional.id}`}
                                type="checkbox"
                                checked={selectedProfessionalIds.includes(professional.id)}
                                onChange={(event) =>
                                  toggleEnablementProfessional(
                                    professional.id,
                                    event.target.checked
                                  )
                                }
                              />
                              <Label
                                className="form-check-label"
                                htmlFor={`enablement-professional-${professional.id}`}
                              >
                                {professional.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="d-flex gap-2 justify-content-end">
                        <Button color="light" onClick={closeForm}>
                          {t("studio.services.actions.cancel")}
                        </Button>
                        <Button
                          color="primary"
                          onClick={saveServiceEnablement}
                          disabled={submitting}
                        >
                          {submitting
                            ? t("studio.services.loading")
                            : t("studio.services.enablement.actions.save")}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Offcanvas isOpen={recipeOpen} toggle={closeRecipe} direction="end" className="w-50">
        <OffcanvasHeader toggle={closeRecipe}>
          {t("studio.services.recipe.title", { name: recipeService?.name ?? "" })}
        </OffcanvasHeader>
        <OffcanvasBody>
          {recipeLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary">{t("studio.services.loading")}</Spinner>
            </div>
          ) : (
            <>
              <p className="text-muted">{t("studio.services.recipe.description")}</p>
              {recipeRows.length === 0 ? (
                <p className="text-muted">{t("studio.services.recipe.empty")}</p>
              ) : (
                recipeRows.map((row) => (
                  <Row key={row.key} className="g-2 align-items-end mb-3">
                    <Col md={7}>
                      <Label>{t("studio.services.recipe.fields.product")}</Label>
                      <Input
                        type="select"
                        value={row.product_id}
                        onChange={(event) =>
                          updateRecipeRow(row.key, { product_id: event.target.value })
                        }
                      >
                        <option value="">{t("studio.services.recipe.fields.productPlaceholder")}</option>
                        {internalProducts.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name} ({t(`studio.products.measure.${product.unit_of_measure}`)})
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={3}>
                      <Label>{t("studio.services.recipe.fields.quantity")}</Label>
                      <Input
                        type="number"
                        min={1}
                        value={row.quantity_consumed}
                        onChange={(event) =>
                          updateRecipeRow(row.key, {
                            quantity_consumed: Number(event.target.value) || 1,
                          })
                        }
                      />
                    </Col>
                    <Col md={2}>
                      <Button
                        color="soft-danger"
                        className="w-100"
                        onClick={() => removeRecipeRow(row.key)}
                        title={t("studio.services.recipe.actions.removeRow")}
                      >
                        <i className="ri-delete-bin-line" />
                      </Button>
                    </Col>
                  </Row>
                ))
              )}
              <div className="d-flex flex-wrap gap-2 justify-content-between mt-3">
                <Button color="soft-primary" onClick={() => setRecipeRows((rows) => [...rows, newRecipeRow()])}>
                  <i className="ri-add-line align-bottom me-1" />
                  {t("studio.services.recipe.actions.addRow")}
                </Button>
                <div className="d-flex gap-2">
                  <Button color="light" onClick={closeRecipe}>
                    {t("studio.services.actions.cancel")}
                  </Button>
                  <Button color="primary" onClick={saveRecipe} disabled={submitting}>
                    {t("studio.services.recipe.actions.save")}
                  </Button>
                </div>
              </div>
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <h4>{t("studio.services.delete.title")}</h4>
            <p className="text-muted mx-4 mb-0">
              {deleteTarget
                ? t("studio.services.delete.description", { name: deleteTarget.name })
                : t("studio.services.delete.fallback")}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("studio.services.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete} disabled={submitting}>
              {t("studio.services.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <CatalogCategoryWizard
        isOpen={categoryWizardOpen}
        onClose={closeCategoryWizard}
        onComplete={completeCategoryWizard}
      />

      <SmartImportWizard
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="services"
        onCompleted={() => {
          void fetchServices();
        }}
      />
    </div>
  );
};

export default ServicesList;
